const LOCAL_STORAGE_PREFIX = 'generatik_last_accessed_';

export const getWatermarkKey = (
  scope: string,
  resourceId: string,
  subresourceId?: string | null,
  userType?: string | null,
): string => {
  let key = `${scope}_${resourceId}${subresourceId ? `_${subresourceId}` : ''}`;
  if (userType != null) {
    const lower = userType.toLowerCase();
    key += `_${lower === 'services' || lower === 'service_provider' ? 'service' : lower}`;
  }
  return key;
};

export const getStoredWatermark = (key: string): string | null => {
  try {
    return localStorage.getItem(LOCAL_STORAGE_PREFIX + key);
  } catch {
    return null;
  }
};

export const setStoredWatermark = (key: string, value: string) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_PREFIX + key, value);
  } catch {
    // Ignore
  }
};

export interface CommentWatermarkResult {
  lastReadAt: string | null;
  /** False when the request failed and the server-side read state is unknown. */
  fromServer: boolean;
}

// Server-side read state is the source of truth (AAD-3069): when the server answered,
// its value wins — even a null ("never read") beats a newer localStorage entry. The
// stored value only fills in while the server is unreachable.
export const resolveEffectiveWatermark = (
  serverWatermark: CommentWatermarkResult,
  storageKey: string,
): string | null =>
  serverWatermark.fromServer ? serverWatermark.lastReadAt : getStoredWatermark(storageKey);

// Channel-scoped keys let each order channel (team, advertiser, services…) track its own
// read state; the legacy location key was shared across channels, which made marking one
// channel hide unread comments in the others (so team was never marked at all).
export const getChannelWatermarkKey = (
  scope: string,
  resourceId: string,
  subresourceId: string | null | undefined,
  channel: string,
): string => `${getWatermarkKey(scope, resourceId, subresourceId)}_ch_${channel}`;

export const setStoredChannelWatermark = (
  scope: string,
  resourceId: string,
  subresourceId: string | null | undefined,
  channel: string,
  value: string,
) => {
  setStoredWatermark(getChannelWatermarkKey(scope, resourceId, subresourceId, channel), value);
};

// Reads the channel-scoped watermark, honoring the legacy shared key so read state
// recorded before the per-channel migration still counts.
export const getStoredChannelWatermark = (
  scope: string,
  resourceId: string,
  subresourceId: string | null | undefined,
  channel: string,
): string | null =>
  maxTimestamp(
    getStoredWatermark(getChannelWatermarkKey(scope, resourceId, subresourceId, channel)),
    getStoredWatermark(getWatermarkKey(scope, resourceId, subresourceId)),
  );

// SERVICES_ORDER read state is per user (`userScope`) and per channel: a location channel is
// keyed by its location id, every other channel by its value. Every campaign-chat surface
// reads and writes through these two, so the key cannot drift between them.
export const getServicesOrderChannelKey = (channel?: string, locationId?: string): string =>
  locationId ?? channel ?? 'default';

export const getStoredServicesOrderWatermark = (
  orderId: string,
  userScope: string,
  channel?: string,
  locationId?: string,
): string | null =>
  getStoredChannelWatermark(
    'SERVICES_ORDER',
    orderId,
    userScope,
    getServicesOrderChannelKey(channel, locationId),
  );

export const setStoredServicesOrderWatermark = (
  orderId: string,
  userScope: string,
  channel: string | undefined,
  locationId: string | undefined,
  value: string,
): void => {
  setStoredChannelWatermark(
    'SERVICES_ORDER',
    orderId,
    userScope,
    getServicesOrderChannelKey(channel, locationId),
    value,
  );
};

export const maxTimestamp = (
  a: string | null | undefined,
  b: string | null | undefined,
): string | null => {
  if (!a) return b ?? null;
  if (!b) return a ?? null;
  try {
    const timeA = new Date(a).getTime();
    const timeB = new Date(b).getTime();
    if (isNaN(timeA)) return b ?? null;
    if (isNaN(timeB)) return a ?? null;
    return timeA > timeB ? a : b;
  } catch {
    return a ?? b ?? null;
  }
};
